import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Text } from "@lendcord/concord";

interface ScoreGaugeProps {
  score: number;
  min?: number;
  max?: number;
  // The bureau's own band label (CrifCheckResult.scoreBand), shown in place
  // of the locally-computed one when the backend provides it — the arc's
  // color still comes from the fixed thresholds below regardless, since
  // those drive the needle/segment geometry.
  bandLabel?: string | null;
}

const RADIUS = 120;
const CENTER = { x: 150, y: 150 };
const STROKE_WIDTH = 22;
const GAP_DEG = 2.5;
const NEEDLE_LENGTH = RADIUS - STROKE_WIDTH / 2 - 6;

// Fixed CRIF/CIBIL risk tiers (300-900 scale) — bands reuse the app's own
// error/warning/success solids (see packages/concord/src/tokens/colors.ts)
// rather than introducing new hex values just for this widget. The
// warning<->success pair reads close in hue for full-color vision and the
// whole trio collapses for red-green color blindness (an inherent limit of
// any traffic-light-style gauge, flagged by the dataviz skill's palette
// validator) — mitigated the way the skill requires: color is never the only
// signal here, the band name and numeric score are always printed too (see
// docs/design-system.md's "Color is never the only conveyor of meaning").
const BANDS = [
  { toScore: 649, color: "var(--ds-color-error-solid, #B8483A)", labelId: "lender.onboarding.review.crifScoreBandPoor" },
  { toScore: 749, color: "var(--ds-color-warning-solid, #B48A2E)", labelId: "lender.onboarding.review.crifScoreBandFair" },
  { toScore: Infinity, color: "var(--ds-color-success-solid, #617E30)", labelId: "lender.onboarding.review.crifScoreBandGood" },
];

function toRad(deg: number) {
  return (deg * Math.PI) / 180;
}

// Score fraction -> polar angle: 0 (min) sits at 180° (west), 1 (max) sits at
// 0° (east), sweeping clockwise over the top — a standard semicircle gauge.
function fractionToAngle(fraction: number) {
  return 180 - 180 * fraction;
}

function polarPoint(angleDeg: number, radius: number) {
  const rad = toRad(angleDeg);
  return { x: CENTER.x + radius * Math.cos(rad), y: CENTER.y - radius * Math.sin(rad) };
}

function describeArc(startFraction: number, endFraction: number) {
  const startAngle = fractionToAngle(startFraction);
  const endAngle = fractionToAngle(endFraction);
  const start = polarPoint(startAngle, RADIUS);
  const end = polarPoint(endAngle, RADIUS);
  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 0 1 ${end.x} ${end.y}`;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const listener = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, []);
  return reduced;
}

// Animates 0 -> value over ~1.1s with an ease-out curve — skipped in favor of
// the final value immediately when the OS-level reduce-motion setting is on.
function useCountUp(target: number, durationMs = 1100) {
  const [value, setValue] = useState(target);
  const reducedMotion = usePrefersReducedMotion();
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (reducedMotion) {
      setValue(target);
      return;
    }
    setValue(0);
    const start = performance.now();
    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    }
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, reducedMotion, durationMs]);

  return value;
}

export function ScoreGauge({ score, min = 300, max = 900, bandLabel }: ScoreGaugeProps) {
  const intl = useIntl();
  const reducedMotion = usePrefersReducedMotion();
  const clampedScore = Math.min(Math.max(score, min), max);
  const fraction = (clampedScore - min) / (max - min);
  const displayScore = useCountUp(clampedScore);

  const band = BANDS.find((candidate) => clampedScore <= candidate.toScore) ?? BANDS[BANDS.length - 1];

  // Needle rests at the min (pointing left, no rotation) on first paint, then
  // animates to its real angle a tick later so the CSS transition actually
  // has a "from" state to animate from.
  const [needleDelta, setNeedleDelta] = useState(reducedMotion ? 180 - fractionToAngle(fraction) : 0);
  useEffect(() => {
    if (reducedMotion) {
      setNeedleDelta(180 - fractionToAngle(fraction));
      return;
    }
    const timeout = setTimeout(() => setNeedleDelta(180 - fractionToAngle(fraction)), 50);
    return () => clearTimeout(timeout);
  }, [fraction, reducedMotion]);

  const bandBoundaries = [min, 650, 750, max];
  const arcSegments = bandBoundaries.slice(0, -1).map((start, index) => {
    const end = bandBoundaries[index + 1];
    const startFraction = (start - min) / (max - min) + (index === 0 ? 0 : GAP_DEG / 180 / 2);
    const endFraction = (end - min) / (max - min) - (index === bandBoundaries.length - 2 ? 0 : GAP_DEG / 180 / 2);
    return { path: describeArc(startFraction, endFraction), color: BANDS[index].color };
  });

  const needleBase = polarPoint(180, NEEDLE_LENGTH);

  return (
    <div className="flex flex-col items-center gap-3 py-2">
      <svg viewBox="-20 0 340 180" className="w-full max-w-[280px]" role="img" aria-label={`${clampedScore} out of ${max}`}>
        {arcSegments.map((segment) => (
          <path
            key={segment.color}
            d={segment.path}
            fill="none"
            stroke={segment.color}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
          />
        ))}

        <line
          x1={CENTER.x}
          y1={CENTER.y}
          x2={needleBase.x}
          y2={needleBase.y}
          stroke="currentColor"
          className="text-text"
          strokeWidth={5}
          strokeLinecap="round"
          style={{
            transform: `rotate(${needleDelta}deg)`,
            transformOrigin: `${CENTER.x}px ${CENTER.y}px`,
            transition: reducedMotion ? undefined : "transform 1.1s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
        <circle cx={CENTER.x} cy={CENTER.y} r={9} className="fill-text" />

        <text x={CENTER.x - RADIUS} y={CENTER.y + 20} textAnchor="middle" className="fill-text-subtle text-xs">
          {min}
        </text>
        <text x={CENTER.x + RADIUS} y={CENTER.y + 20} textAnchor="middle" className="fill-text-subtle text-xs">
          {max}
        </text>
      </svg>

      <div className="flex flex-col items-center">
        <span className="text-5xl font-bold leading-none text-text">{displayScore}</span>
        <Text as="p" size="sm" variant="muted" className="mt-2">
          {intl.formatMessage({ id: "lender.onboarding.review.crifScoreLabel" })}
        </Text>
        <span className="mt-1 text-sm font-medium" style={{ color: band.color }}>
          {bandLabel || intl.formatMessage({ id: band.labelId })}
        </span>
      </div>
    </div>
  );
}
